import {ApplicationStoreType} from 'constants/flow/flowTypes';
import {createSelector} from 'reselect';
import {pickAll} from 'ramda';

import {
    getPrescriptionValues,
    PrescriptionValuesType,
} from 'features/contacts/reducers/prescriptionValues/prescriptionValues';
import {ContactLensPrescriptionType, ContactColorsType} from 'features/contacts/constants/types';

/**
 * Returns fields for current lenses
 * @param {ApplicationStoreType} store - store
 * @return {ContactLensPrescriptionType} - fields from wizard
 */
export const getFields = (store: ApplicationStoreType): ContactLensPrescriptionType =>
    store.wizard.contact_lens_prescription;

/**
 * Returns labels and values for color fields
 * @param {ApplicationStoreType} store - store
 * @return {ContactColorsType} - color values
 */
export const getColors = (store: ApplicationStoreType): ContactColorsType =>
    store.wizard.contact_lens_colors || [];

/**
 * Returns combined fields with colors to provide only
 * those fields what is need for current product
 * (we have all fields in reducer)
 */
export const getCombinedFields = createSelector(
    getFields,
    getColors,
    (
        wizardFields,
        wizardColors,
    ): ContactLensPrescriptionType & {color?: Array<{option: string; clean_option: string}>} => ({
        ...wizardFields,
        ...(wizardColors.length
            ? {color: wizardColors.map(({value}) => ({option: value, clean_option: value}))}
            : {}),
    }),
);

/**
 * Return only fields which needed for current product, combined with colors
 */
export const getAvailablePrescriptionFields = createSelector(
    getCombinedFields,
    getPrescriptionValues,
    (
        combinedFields,
        prescriptionValues,
    ): Pick<PrescriptionValuesType, 'od'> & Pick<PrescriptionValuesType, 'os'> => ({
        od: pickAll(Object.keys(combinedFields), prescriptionValues.od),
        os: pickAll(Object.keys(combinedFields), prescriptionValues.os),
    }),
);

/**
 * Return left and right side count of boxes
 */
export const getCountOfBoxes = createSelector(
    getPrescriptionValues,
    ({od, os}): number => Number(od.boxes) + Number(os.boxes),
);
